/**
 * The text of one PDF page, with its paragraphs. pdf.js gives text items with a position, and the
 * file has no notion of paragraph: a visual line is all it knows. The paragraphs are rebuilt from
 * the geometry of the lines (the gap to the line before, the size and the font, the indent of
 * the first line), with a little text (a sentence that ends on a short line). When the geometry
 * is missing or makes no sense, the text heuristic of `recollerLignes` is used instead.
 */
import {
  commenceParUneMajuscule,
  commenceParUnePuce,
  finitUnePhrase,
  joindreLignes,
  peutEtreUnTitre,
  recollerLignes,
} from '@/lib/recoller-lignes';

/** The part of a pdf.js text item that is used here. */
export interface ElementTextePdf {
  str: string;
  transform?: readonly number[] | undefined;
  width?: number | undefined;
  height?: number | undefined;
  fontName?: string | undefined;
  hasEOL?: boolean | undefined;
}

interface LignePdf {
  texte: string;
  x: number;
  y: number;
  taille: number;
  police: string;
  largeur: number;
}

/** A gap to the line before over this many normal line spacings starts a paragraph. */
const ECART_PARAGRAPHE = 1.4;
/** A first line indented by more than this many font sizes starts a paragraph. */
const RETRAIT_PARAGRAPHE = 0.8;
/** A change of size over this share of it (a title) starts a paragraph. */
const CHANGEMENT_TAILLE = 0.1;
/** The last line of a paragraph is shorter than this share of the widest line. */
const LIGNE_FINALE = 0.8;
/** A line shorter than this share of the widest one, with no end of sentence, is a title. */
const TITRE_COURT = 0.65;

function geometrieValide(transform: readonly number[] | undefined): transform is number[] {
  return transform !== undefined && transform.length >= 6 && transform.every(Number.isFinite);
}

/** The visual lines of a page, or `null` when an item has no usable position. */
function versLignes(elements: readonly ElementTextePdf[]): LignePdf[] | null {
  const lignes: Array<{
    morceaux: Array<{ str: string; police: string }>;
    x: number;
    y: number;
    taille: number;
    fin: number;
  }> = [];
  let cassure = true;
  let espacePendant = false;
  for (const element of elements) {
    // An empty item or one made of spaces has no position worth trusting (a blank item set a few
    // points off its line would cut the line in two): it only breaks the line or is a space.
    if (element.str.trim() === '') {
      if (element.hasEOL === true) cassure = true;
      else if (element.str !== '') espacePendant = true;
      continue;
    }
    if (!geometrieValide(element.transform)) return null;
    const [, , c, d, x, y] = element.transform as [number, number, number, number, number, number];
    const taille = Math.abs(element.height ?? 0) || Math.hypot(c, d);
    if (!(taille > 0)) return null;
    const largeur = Number.isFinite(element.width) ? (element.width as number) : 0;
    const courante = lignes[lignes.length - 1];
    const police = element.fontName ?? '';
    if (courante === undefined || cassure || Math.abs(y - courante.y) > 0.4 * courante.taille) {
      lignes.push({ morceaux: [{ str: element.str, police }], x, y, taille, fin: x + largeur });
    } else {
      const dernier = courante.morceaux[courante.morceaux.length - 1]?.str ?? '';
      const trou = x - courante.fin > 0.15 * taille;
      const espace =
        (trou || espacePendant) && !/\s$/.test(dernier) && !/^\s/.test(element.str) ? ' ' : '';
      courante.morceaux.push({ str: espace + element.str, police });
      courante.taille = Math.max(courante.taille, taille);
      courante.fin = x + largeur;
    }
    cassure = element.hasEOL === true;
    espacePendant = false;
  }
  return lignes
    .map((ligne) => {
      const parPolice = new Map<string, number>();
      for (const { str, police } of ligne.morceaux) {
        parPolice.set(police, (parPolice.get(police) ?? 0) + str.trim().length);
      }
      const police = [...parPolice.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
      return {
        texte: ligne.morceaux
          .map((m) => m.str)
          .join('')
          .trim(),
        x: ligne.x,
        y: ligne.y,
        taille: ligne.taille,
        police,
        largeur: Math.max(0, ligne.fin - ligne.x),
      };
    })
    .filter((ligne) => ligne.texte !== '');
}

function mediane(valeurs: number[]): number {
  const triees = [...valeurs].sort((a, b) => a - b);
  return triees[Math.floor(triees.length / 2)] ?? 0;
}

/** The most common left edge of the lines. */
function margeGauche(lignes: readonly LignePdf[]): number {
  const comptes = new Map<number, number>();
  for (const { x } of lignes) comptes.set(Math.round(x), (comptes.get(Math.round(x)) ?? 0) + 1);
  return [...comptes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 0;
}

/** The units (paragraphs and list items) of a page, each one as its visual lines; `null` without sense. */
function decouperParGeometrie(lignes: readonly LignePdf[]): string[][] | null {
  const ecarts: number[] = [];
  for (let i = 1; i < lignes.length; i++) {
    const avant = lignes[i - 1];
    const ligne = lignes[i];
    if (avant === undefined || ligne === undefined) continue;
    const ecart = avant.y - ligne.y;
    if (ecart > 0 && ecart < 3 * ligne.taille && Math.abs(avant.taille - ligne.taille) < 0.5) {
      ecarts.push(ecart);
    }
  }
  if (ecarts.length === 0) return null;
  const interligne = mediane(ecarts);
  const marge = margeGauche(lignes);
  const plusLarge = lignes.reduce((max, ligne) => Math.max(max, ligne.largeur), 0);

  const unites: string[][] = [];
  for (const [i, ligne] of lignes.entries()) {
    const avant = lignes[i - 1];
    let nouvelle = avant === undefined || commenceParUnePuce(ligne.texte);
    if (!nouvelle && avant !== undefined) {
      const ecart = avant.y - ligne.y;
      nouvelle =
        ecart <= 0 ||
        ecart > ECART_PARAGRAPHE * interligne ||
        Math.abs(ligne.taille - avant.taille) > CHANGEMENT_TAILLE * avant.taille ||
        ligne.police !== avant.police ||
        (ligne.x - marge > RETRAIT_PARAGRAPHE * ligne.taille &&
          avant.x - marge <= RETRAIT_PARAGRAPHE * avant.taille) ||
        (avant.largeur < TITRE_COURT * plusLarge &&
          peutEtreUnTitre(avant.texte) &&
          commenceParUneMajuscule(ligne.texte)) ||
        (finitUnePhrase(avant.texte) &&
          avant.largeur < LIGNE_FINALE * plusLarge &&
          commenceParUneMajuscule(ligne.texte));
    }
    if (nouvelle) unites.push([ligne.texte]);
    else unites[unites.length - 1]?.push(ligne.texte);
  }
  // A text where every line is its own unit is not a text the geometry understood.
  return lignes.length > 3 && unites.length === lignes.length ? null : unites;
}

/** The lines as pdf.js gives them (one per `hasEOL`), then the text heuristic: the way before the geometry. */
function texteSansGeometrie(elements: readonly ElementTextePdf[]): string {
  let brut = '';
  for (const element of elements) {
    brut += typeof element.str === 'string' ? element.str : '';
    if (element.hasEOL === true) brut += '\n';
  }
  try {
    return recollerLignes(brut.trim());
  } catch {
    return brut.trim();
  }
}

/** The text of a page by the geometry, or `null` when the geometry does not make sense. */
function texteParGeometrie(elements: readonly ElementTextePdf[]): string | null {
  const lignes = versLignes(elements);
  if (lignes === null) return null;
  if (lignes.length <= 1) return lignes[0]?.texte ?? '';
  const unites = decouperParGeometrie(lignes);
  if (unites === null) return null;
  let texte = '';
  let precedentePuce = false;
  for (const unite of unites) {
    const puce = commenceParUnePuce(unite[0] ?? '');
    const paragraphe = joindreLignes(unite);
    texte += texte === '' ? paragraphe : `${puce && precedentePuce ? '\n' : '\n\n'}${paragraphe}`;
    precedentePuce = puce;
  }
  return texte;
}

/**
 * The text of a page: paragraphs separated by a blank line, list items by a line break. The
 * geometry is a refinement and never a reason to fail (#29): any exception, or a result that
 * has lost text, falls back on the lines as pdf.js gives them.
 */
export function textePageDepuisElements(elements: readonly ElementTextePdf[]): string {
  try {
    const texte = texteParGeometrie(elements);
    if (typeof texte === 'string' && (texte !== '' || !elements.some((e) => e.str?.trim()))) {
      return texte;
    }
  } catch {
    // Falls through to the text of the lines.
  }
  return texteSansGeometrie(elements);
}
